/**
 * Utilidades para filtrado de fechas
 */

/**
 * Verifica si una fecha es del día actual
 */
export const isToday = (dateString: string): boolean => {
  try {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const date = new Date(dateString)
    date.setHours(0, 0, 0, 0)

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString)
      return false
    }

    return date.getTime() === today.getTime()
  } catch (error) {
    console.error('Error parsing date:', dateString, error)
    return false
  }
}

/**
 * Verifica si una fecha es de ayer
 */
export const isYesterday = (dateString: string): boolean => {
  try {
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    yesterday.setHours(0, 0, 0, 0)

    const date = new Date(dateString)
    date.setHours(0, 0, 0, 0)

    if (isNaN(date.getTime())) {
      return false
    }

    return date.getTime() === yesterday.getTime()
  } catch (error) {
    return false
  }
}

/**
 * Obtiene el inicio del día actual (00:00:00)
 */
export const getStartOfToday = (): Date => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today
}

/**
 * Obtiene el fin del día actual (23:59:59)
 */
export const getEndOfToday = (): Date => {
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  return today
}

/**
 * Filtra órdenes para mostrar solo las del día actual
 */
export const filterTodayOrders = <
  T extends {
    orderDate?: string
    orderDateISO?: string
    order_date?: string
    created_at?: string
  },
>(
  orders: T[],
): T[] => {
  return orders.filter((order) => {
    // Prefer ISO format for accurate filtering
    const dateToCheck =
      order.orderDateISO ||
      order.order_date ||
      order.created_at ||
      order.orderDate

    if (!dateToCheck) {
      console.warn('Order has no date field:', order)
      return false
    }

    const result = isToday(dateToCheck)

    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      console.log('Filtering order:', {
        dateToCheck,
        isToday: result,
        orderDateISO: order.orderDateISO,
        orderDate: order.orderDate,
      })
    }

    return result
  })
}

/**
 * Formatea una fecha a formato legible en español
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Formatea una fecha con hora
 */
export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Obtiene el rango de fechas para la semana actual
 */
export const getCurrentWeekRange = (): { start: Date; end: Date } => {
  const today = new Date()
  const dayOfWeek = today.getDay()
  const start = new Date(today)
  start.setDate(today.getDate() - dayOfWeek)
  start.setHours(0, 0, 0, 0)

  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  end.setHours(23, 59, 59, 999)

  return { start, end }
}

/**
 * Obtiene el rango de fechas para el mes actual
 */
export const getCurrentMonthRange = (): { start: Date; end: Date } => {
  const today = new Date()
  const start = new Date(today.getFullYear(), today.getMonth(), 1)
  const end = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  )

  return { start, end }
}
